import { Employee } from '@/api/apiEmployees';
import { Avatar, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { renderCategoryPills, renderRolePills } from '@/components/ui/table-pill';
import { EMPLOYEE_PAGE_ROLES, type EmployeePageRole } from '@/lib/AuthorizedRoles';
import { ColumnDef } from '@tanstack/react-table';
import { EmployeeDataTableRowActions } from './employee-data-table-row-actions';

interface ColumnActions {
  onEdit: (employee: Employee) => void;
  onDelete: (empUserId: string) => void;
  onSwapPosition: (currentRow: Employee, targetRow: Employee, direction: 'up' | 'down') => void;
}

// Base columns that are always visible
const getBaseColumns = ({
  onEdit,
  onDelete,
  onSwapPosition,
}: ColumnActions): ColumnDef<Employee>[] => [
  {
    accessorKey: 'position',
    enableHiding: true,
  },
  {
    id: 'actions',
    size: 44,
    minSize: 44,
    maxSize: 44,
    cell: ({ row, table }) => (
      <div className="flex w-full items-center justify-center">
        <EmployeeDataTableRowActions
          employee={row.original}
          table={table}
          onEdit={onEdit}
          onDelete={onDelete}
          onSwapPosition={onSwapPosition}
        />
      </div>
    ),
  },
  {
    accessorKey: 'empName',
    header: 'Name',
    size: 240,
    minSize: 180,
    cell: ({ row }) => (
      <div className="flex items-center gap-3 min-w-0">
        <Avatar className="h-9 w-9 shrink-0 shadow-sm ring-1 ring-black/5">
          <AvatarImage src={row.original.empAvatar || '/puritynailbar_svg.png'} />
        </Avatar>
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-sm font-medium">{row.getValue('empName')}</span>
          <span className="truncate text-xs text-muted-foreground">{row.original.empEmail}</span>
        </div>
      </div>
    ),
  },
  {
    accessorKey: 'empRole',
    header: 'Role',
    size: 120,
    minSize: 100,
    cell: ({ row }) => renderRolePills(row.getValue('empRole') as string[] | null),
  },
  {
    accessorKey: 'empCategory',
    header: 'Category',
    size: 110,
    minSize: 90,
    cell: ({ row }) => renderCategoryPills(row.getValue('empCategory')),
  },
  {
    accessorKey: 'empSpecialCategory',
    header: 'Special',
    size: 110,
    minSize: 90,
    cell: ({ row }) => renderCategoryPills(row.getValue('empSpecialCategory')),
  },
  {
    accessorKey: 'empStatus',
    header: 'Status',
    size: 88,
    minSize: 80,
    maxSize: 100,
    cell: ({ row }) => {
      const isActive = Boolean(row.getValue('empStatus'));
      return isActive ? (
        <Badge
          className="rounded-full border-0 bg-[#B56A3A] px-2.5 py-0.5 text-xs font-medium text-white hover:bg-[#B56A3A]"
          size="small"
        >
          Active
        </Badge>
      ) : (
        <Badge
          variant="outline"
          className="rounded-full px-2.5 py-0.5 text-xs font-medium"
          size="small"
        >
          Inactive
        </Badge>
      );
    },
  },
];

// Commission columns that are only visible for authorized users
const getCommissionColumns = (): ColumnDef<Employee>[] => [
  {
    accessorKey: 'empCommission',
    header: 'Comm %',
    size: 72,
    minSize: 64,
    maxSize: 88,
    cell: ({ row }) => (
      <span className="text-sm tabular-nums">{row.getValue('empCommission')}</span>
    ),
  },
  {
    accessorKey: 'empCheckPercent',
    header: 'Check %',
    size: 72,
    minSize: 64,
    maxSize: 88,
    cell: ({ row }) => (
      <span className="text-sm tabular-nums">{row.getValue('empCheckPercent')}</span>
    ),
  },
];

export const columns = (actions: ColumnActions, userRoles?: string[]): ColumnDef<Employee>[] => {
  const baseColumns = getBaseColumns(actions);
  const hasAccess = userRoles?.some(role => EMPLOYEE_PAGE_ROLES.includes(role as EmployeePageRole));

  if (!hasAccess) return baseColumns;
  return [...baseColumns, ...getCommissionColumns()];
};
